import { FC, ReactElement } from "react";
import { Auth0Provider, User } from "@auth0/auth0-react";
import { MsalProvider } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import {
  msalInstance,
  auth0Config,
  keyCloakInitOptions,
  keycloak
} from "common/config/auth.config";
import { ENV_CONFIG } from "common/config/env.config";
import { AuthMode, GenericStatus } from "common/enums";
import { KeycloakProvider } from "../Keycloak";
import { useAppDispatch } from "common/redux/core/root.reducer";
import { appActions } from "common/redux/app/app.slice";

type AuthProviderProps = {
  children: ReactElement;
};

interface Auth0AppState {
  returnTo?: string;
  [key: string]: unknown;
}

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const authMode = (ENV_CONFIG.AUTH_MODE as AuthMode) || AuthMode.AAD;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: Auth0AppState, user?: User) => {
    if (user?.email) {
      dispatch(appActions.updateLoginActivityStatus(GenericStatus.PENDING));
    }
    // Navigate to the original URL that user was trying to access
    if (appState?.returnTo) {
      navigate(appState.returnTo, { replace: true });
    }
  };
  const getAuthProvider = () => {
    // Always wrap with MsalProvider (using real config) to prevent useMsal() hook errors
    // in shared components that call useMsal() regardless of auth mode.
    switch (authMode) {
      case AuthMode.AUTH0:
        return (
          <MsalProvider instance={msalInstance}>
            <Auth0Provider
              {...auth0Config}
              onRedirectCallback={onRedirectCallback}
            >
              {children}
            </Auth0Provider>
          </MsalProvider>
        );
      case AuthMode.KEYCLOAK:
        return (
          <MsalProvider instance={msalInstance}>
            <KeycloakProvider
              client={keycloak}
              initOptions={keyCloakInitOptions}
            >
              {children}
            </KeycloakProvider>
          </MsalProvider>
        );
      case AuthMode.AAD:
      default:
        return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
    }
  };

  return getAuthProvider();
};

export default AuthProvider;
